/**
 * Summary text helpers for accommodation editor accordion sections.
 * Keeps summary logic out of JSX.
 */

import type { PrepaymentPolicy, PrepaymentType } from '@/lib/booking/prepayment';

/* ── Bokningar ── */

export function getBookingSettingsSummary(opts: {
  bookingMode: string;
  deadlineHours: string;
  checkIn: string;
  checkOut: string;
}): string {
  const parts: string[] = [];

  if (opts.bookingMode === 'required') {
    parts.push('Bokning krävs');
  } else {
    parts.push('Bokning avstängd');
  }

  const h = parseInt(opts.deadlineHours, 10);
  if (h > 0) {
    parts.push(`senast ${h} h innan`);
  }

  if (opts.checkIn && opts.checkOut) {
    parts.push(`${opts.checkIn}–${opts.checkOut}`);
  }

  return parts.join(' · ');
}

/* ── Betalning ── */

export function getPrepaymentSettingsSummary(opts: {
  policy: PrepaymentPolicy;
  type: PrepaymentType;
  percentage: string;
  fixedKr: string;
}): string {
  if (opts.policy === 'none') {
    return 'Ingen förskottsbetalning som standard';
  }

  const policyLabel = opts.policy === 'required'
    ? 'Förskott krävs som standard'
    : 'Förskott kan begäras';

  if (opts.type === 'percentage') {
    const pct = parseInt(opts.percentage, 10) || 60;
    return `${policyLabel} · ${pct} % av totalbeloppet`;
  }

  const kr = parseInt(opts.fixedKr, 10);
  if (kr > 0) {
    return `${policyLabel} · ${kr} kr`;
  }

  return `${policyLabel} · belopp ej angivet`;
}

/* ── För gästen ── */

export function getGuestInfoSummary(opts: {
  houseRules: string;
  descriptionShort: string;
}): string {
  const parts: string[] = [];

  if (opts.houseRules.trim()) {
    parts.push('Husregler tillagda');
  } else {
    parts.push('Inga husregler');
  }

  if (opts.descriptionShort.trim()) {
    parts.push('Kort beskrivning');
  }

  return parts.join(' · ');
}

/* ── Tillgänglighet ── */

export function getAvailabilitySummary(blocksCount: number): string {
  if (blocksCount === 0) return 'Inga blockerade datum';
  if (blocksCount === 1) return '1 blockerad period';
  return `${blocksCount} blockerade perioder`;
}

/* ── Grunduppgifter ── */

export function getBasicInfoSummary(opts: {
  title: string;
  beds: string;
  guestCapacity: string;
  priceText: string;
}): string {
  const parts: string[] = [];
  if (opts.title.trim()) parts.push(opts.title.trim());
  if (opts.beds) parts.push(`${opts.beds} bäddar`);
  if (opts.guestCapacity) parts.push(`max ${opts.guestCapacity} gäster`);
  if (opts.priceText.trim()) parts.push(`${opts.priceText.trim()} kr/natt`);
  return parts.length > 0 ? parts.join(' · ') : 'Ange grunduppgifter';
}
